import React, { FC, useEffect, useState } from 'react';
import {
  BarWrapper,
  PinCont,
  PinTriangle,
  ScoreBubble,
  Tick,
  TicksRow,
  Track,
  TrackSegment,
} from './EffectMeter.styles';
import {
  EFFECT_SCORE_COLORS,
  getScoreColor,
} from '../../../../../helpers/effectReports/chartCounters';

interface IEffectMeter {
  value: number;
  thresholdValues: {
    high: number;
    low: number;
    medium: number;
  };
}

const MIN_VALUE = 1;
const MAX_VALUE = 7;

const getPercentage = (value: number) => {
  const percentage = ((value - MIN_VALUE) / (MAX_VALUE - MIN_VALUE)) * 100;
  return Math.min(100, Math.max(0, percentage));
};

const EffectMeter: FC<IEffectMeter> = ({ value, thresholdValues }) => {
  const { low, medium, high } = thresholdValues;

  const segments = [
    { name: 'belowLow', width: low - MIN_VALUE, color: EFFECT_SCORE_COLORS.belowLow },
    { name: 'lowToMedium', width: medium - low, color: EFFECT_SCORE_COLORS.lowToMedium },
    { name: 'mediumToHigh', width: high - medium, color: EFFECT_SCORE_COLORS.mediumToHigh },
    { name: 'aboveHigh', width: MAX_VALUE - high, color: EFFECT_SCORE_COLORS.aboveHigh },
  ].map(segment => ({
    ...segment,
    width: (Math.max(0, segment.width) / (MAX_VALUE - MIN_VALUE)) * 100,
  }));

  const ticks: { position: number; label: string; align: 'start' | 'middle' | 'end' }[] = [
    { position: 0, label: `${MIN_VALUE}`, align: 'start' },
    { position: getPercentage(low), label: `${low}`, align: 'middle' },
    { position: getPercentage(medium), label: `${medium - 0.1}`, align: 'middle' },
    { position: getPercentage(high), label: `${high}`, align: 'middle' },
    { position: 100, label: `${MAX_VALUE}`, align: 'end' },
  ];

  const scoreColor = getScoreColor(value, low, high, medium);

  const [pinPosition, setPinPosition] = useState(0);

  useEffect(() => {
    let timeoutId: number;

    timeoutId = window.setTimeout(() => {
      setPinPosition(getPercentage(value));
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [value]);

  return (
    <BarWrapper>
      <PinCont position={pinPosition}>
        <ScoreBubble data-testid="meter-bubble" color={scoreColor}>
          {value.toFixed(1)}
        </ScoreBubble>
        <PinTriangle color={scoreColor} />
      </PinCont>
      <Track>
        {segments.map(({ name, width, color }, index) => (
          <TrackSegment
            key={name}
            data-testid="meter-segment"
            width={width}
            color={color}
            withDivider={index < segments.length - 1}
          />
        ))}
      </Track>
      <TicksRow>
        {ticks.map(({ position, label, align }, index) => (
          <Tick key={`${label}-${index}`} position={position} align={align}>
            {label}
          </Tick>
        ))}
      </TicksRow>
    </BarWrapper>
  );
};

export default EffectMeter;
